import useSWRMutation from 'swr/mutation'
import {
  type SupplierChangeRequestDto,
  type SupplierDto
} from '@amici/myamici-integrations-client'
import useApi from '../../common/hooks/useApi'
import useSWR from 'swr'
import { useCallback } from 'react'
import ReactGA from 'react-ga4'

interface UseSupplierHook {
  data: SupplierDto | undefined
  update: (
    supplierData: SupplierChangeRequestDto
  ) => Promise<SupplierDto | undefined>
  create: () => Promise<SupplierDto | undefined>
  isLoading: boolean
  isMutating: boolean
  isValidating: boolean
  hasData: boolean
  loadingError: any
  mutationError: any
}

function useSupplier (supplierId?: string): UseSupplierHook {
  const { suppliersApi, fetcher } = useApi()

  const {
    data,
    isLoading,
    isValidating,
    error: loadingError
  } = useSWR(
    supplierId,
    async () =>
      await fetcher(suppliersApi.getSupplierById, { id: supplierId ?? '' })
  )

  const {
    trigger,
    isMutating,
    error: mutationError
  } = useSWRMutation(
    supplierId,
    async (_, { arg }: { arg: SupplierChangeRequestDto }) =>
      await fetcher(suppliersApi.modifySupplier, {
        id: supplierId ?? '',
        supplierChangeRequestDto: arg
      }),
    { revalidate: false }
  )

  const { trigger: newTrigger } = useSWRMutation(
    'new_supplier',
    async _ =>
      await fetcher(suppliersApi.createSupplier, {
        supplierChangeRequestDto: {}
      }),
    { revalidate: false }
  )

  const update = useCallback(
    async (
      supplierData: SupplierChangeRequestDto
    ): Promise<SupplierDto | undefined> => {
      if (!supplierId) {
        return
      }

      const result = await trigger(supplierData)
      ReactGA.event('submit_form', {
        form_id: 'supplier_form',
        supplier_id: supplierId
      })

      return result
    },
    [supplierId, trigger]
  )

  const create = useCallback(async (): Promise<SupplierDto | undefined> => {
    const result = await newTrigger()

    ReactGA.event('create_new_supplier', {
      form_id: 'supplier_form',
      supplier_id: result.id
    })

    return result
  }, [newTrigger])

  const hasData = !!data?.id

  return {
    data,
    create,
    update,
    isLoading,
    isMutating,
    isValidating,
    hasData,
    loadingError,
    mutationError
  }
}

export default useSupplier
